import { type JSX, useId } from 'react';

import type { Verstoss } from '../api/fehler';

/**
 * Ein beschriftetes Eingabefeld mit Hilfe und Feldfehlern.
 *
 * Drei Anforderungen stecken darin, und alle drei sind hier leichter zu
 * erfuellen als in jedem Formular einzeln:
 *
 *   ERR-03  Der Fehler steht AM Feld und nicht als globaler Kasten. Er ist
 *           ueber aria-describedby mit dem Feld verbunden, damit ein
 *           Vorleseprogramm ihn beim Betreten des Feldes mitliest — und nicht
 *           erst, wenn der Anwender ihn zufaellig findet.
 *   ERR-04  ALLE Verstoesse zu diesem Feld, nicht nur der erste. Ein Passwort
 *           kann gleichzeitig zu kurz und wiederverwendet sein.
 *   NFA-07  Ein <label for=...> macht die Beschriftung anklickbar und den
 *           Zusammenhang fuer Hilfsmittel eindeutig.
 *   NFA-17  Kontextbezogene Hilfe direkt am Feld statt in einem Handbuch, das
 *           niemand aufschlaegt.
 *
 * `aria-invalid` und die rote Umrandung gehen zusammen, aber die Farbe ist
 * nie das einzige Merkmal: Der Fehlertext steht daneben (WCAG 1.4.1).
 */
export function Feld({
  beschriftung,
  wert,
  aendern,
  art = 'text',
  hilfe,
  verstoesse = [],
  pflicht = false,
  automatik,
  hinweisText,
  eingabeart,
  ohneAutokorrektur = false,
  verweis,
}: {
  readonly beschriftung: string;
  readonly wert: string;
  readonly aendern: (neu: string) => void;
  /**
   * `email` schaltet auf Mobilgeraeten die Tastatur mit dem At-Zeichen ein.
   * Geprueft wird trotzdem fachlich: Alle Formulare tragen `noValidate`,
   * weil die Sprechblase des Browsers weder die drei Teile aus ERR-01
   * fuehrt noch nach ERR-04 alle Verstoesse gemeinsam nennt.
   */
  readonly art?: 'text' | 'password' | 'email';
  readonly hilfe?: string;
  /** ERR-04: alle Verstoesse zu diesem Feld, nicht nur der erste. */
  readonly verstoesse?: readonly Verstoss[];
  readonly pflicht?: boolean;
  /** Was der Browser ausfuellen darf — bei Passwoertern wichtig. */
  readonly automatik?: string;
  readonly hinweisText?: string;
  /** Blendet die Bildschirmtastatur auf Mobilgeraeten passend ein. */
  readonly eingabeart?: 'numeric' | 'text';
  /**
   * Schaltet Grossschreibung, Autokorrektur und Rechtschreibpruefung ab.
   *
   * FUER ALLES, WAS KEIN WORT IST. Ein Anmeldename ist keines: iOS macht aus
   * `k.ahrens` beim Tippen `K.ahrens` und die Autokorrektur gelegentlich etwas
   * voellig anderes. Der Vergleich in der Datenbank ignoriert die
   * Gross-/Kleinschreibung (`lower(anmeldename) = lower($1)`) — die
   * Autokorrektur ignoriert er nicht.
   *
   * Seit dem 11.09.2026, weil der Trainerbereich hauptsaechlich auf dem
   * Telefon benutzt wird (RES-30).
   */
  readonly ohneAutokorrektur?: boolean;
  readonly verweis?: React.Ref<HTMLInputElement>;
}): JSX.Element {
  const id = useId();
  const hilfeId = `${id}-hilfe`;
  const fehlerId = `${id}-fehler`;
  const fehlerhaft = verstoesse.length > 0;

  // Reihenfolge zaehlt: Der Fehler wird zuerst vorgelesen, die Hilfe danach.
  const beschriebenVon = [fehlerhaft ? fehlerId : undefined, hilfe !== undefined ? hilfeId : undefined]
    .filter((x): x is string => x !== undefined)
    .join(' ');

  return (
    <div className={`feld${fehlerhaft ? ' feld--fehlerhaft' : ''}`}>
      <label className="feld__beschriftung" htmlFor={id}>
        {beschriftung}
        {pflicht && (
          <span className="feld__pflicht" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>

      <input
        id={id}
        ref={verweis}
        className="feld__eingabe"
        type={art}
        value={wert}
        onChange={(ereignis) => aendern(ereignis.target.value)}
        required={pflicht}
        aria-invalid={fehlerhaft}
        {...(beschriebenVon !== '' ? { 'aria-describedby': beschriebenVon } : {})}
        {...(automatik !== undefined ? { autoComplete: automatik } : {})}
        {...(eingabeart !== undefined ? { inputMode: eingabeart } : {})}
        {...(ohneAutokorrektur
          ? { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }
          : {})}
        {...(hinweisText !== undefined ? { placeholder: hinweisText } : {})}
      />

      {fehlerhaft && (
        <div className="feld__fehler" id={fehlerId}>
          {verstoesse.map((verstoss, stelle) => (
            // Der Schluessel enthaelt die Stelle, weil dieselbe Kennung zweimal
            // vorkommen kann — etwa zwei Laengenverstoesse an einem Feld.
            <p className="feld__fehler-satz" key={`${verstoss.kennung}-${String(stelle)}`}>
              {verstoss.meldung.wasGeschah}{' '}
              {/* Alle drei Teile aus ERR-01, auch am Feld. Die Auswirkung
                  gedaempft, damit die Handlungsanweisung im Blick bleibt. */}
              <span className="feld__fehler-auswirkung">{verstoss.meldung.auswirkung}</span>{' '}
              <span className="feld__fehler-schritt">{verstoss.meldung.naechsterSchritt}</span>
            </p>
          ))}
        </div>
      )}

      {hilfe !== undefined && (
        <p className="feld__hilfe" id={hilfeId}>
          {hilfe}
        </p>
      )}
    </div>
  );
}
